import React, { useState, useEffect, useRef } from "react";
import { Box, Button, TextField, Grid } from "@mui/material";
import { Resource } from "../../store/slices/types";
import BasicInformationSection from "./components/BasicInformationSection";
import ResourceConfigurationSection from "./components/ResourceConfigurationSection";
import MediaUploadSection from "./components/MediaUploadSection";
import TagsAndSettingsSection from "./components/TagsAndSettingsSection";

interface ResourceFormProps {
  resource?: Resource | null;
  onSave: (resourceData: Partial<Resource>, mediaFile?: File) => void;
  onCancel: () => void;
  saving?: boolean;
  uploadProgress?: number;
}

const ResourceForm: React.FC<ResourceFormProps> = ({
  resource,
  onSave,
  onCancel,
  saving = false,
  uploadProgress = 0,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedMedia, setSelectedMedia] = useState<File | null>(null);
  const hasInitialMedia =
    (resource?.type === "video" ||
      resource?.type === "audio" ||
      resource?.type === "pdf") &&
    resource?.url;
  const [mediaPreview, setMediaPreview] = useState<string | null>(
    hasInitialMedia && resource?.url ? resource.url : null
  );
  const [isExistingMedia, setIsExistingMedia] = useState<boolean>(
    !!hasInitialMedia
  );
  const [formData, setFormData] = useState({
    title: resource?.title || "",
    description: resource?.description || "",
    shortDescription: resource?.shortDescription || "",
    type: resource?.type || "article",
    category: resource?.category || "general",
    url: resource?.url || "",
    content: resource?.content || "",
    duration: resource?.duration || 0,
    author: resource?.author || "",
    source: resource?.source || "",
    tags: resource?.tags?.join(", ") || "",
    isActive: resource?.isActive !== undefined ? resource.isActive : true,
    color: resource?.color || "#667eea",
  });

  useEffect(() => {
    setFormData({
      title: resource?.title || "",
      description: resource?.description || "",
      shortDescription: resource?.shortDescription || "",
      type: resource?.type || "article",
      category: resource?.category || "general",
      url: resource?.url || "",
      content: resource?.content || "",
      duration: resource?.duration || 0,
      author: resource?.author || "",
      source: resource?.source || "",
      tags: resource?.tags?.join(", ") || "",
      isActive: resource?.isActive !== undefined ? resource.isActive : true,
      color: resource?.color || "#667eea",
    });

    // Clean up previous object URL if it was a newly selected file
    const prevPreview = mediaPreview;
    const prevIsExisting = isExistingMedia;

    const hasMedia =
      (resource?.type === "video" ||
        resource?.type === "audio" ||
        resource?.type === "pdf") &&
      resource?.url;
    setMediaPreview(hasMedia && resource?.url ? resource.url : null);
    setIsExistingMedia(!!hasMedia);
    setSelectedMedia(null);
    setErrors({});

    // Clean up old object URL after state update
    if (
      prevPreview &&
      !prevIsExisting &&
      prevPreview !== (hasMedia && resource?.url ? resource.url : null)
    ) {
      URL.revokeObjectURL(prevPreview);
    }
  }, [resource]);

  // Cleanup object URL on unmount or when media changes
  useEffect(() => {
    return () => {
      if (
        mediaPreview &&
        !isExistingMedia &&
        mediaPreview.startsWith("blob:")
      ) {
        URL.revokeObjectURL(mediaPreview);
      }
    };
  }, [mediaPreview, isExistingMedia]);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleMediaSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const isVideo = formData.type === "video";
      const isAudio = formData.type === "audio";
      const isPdf = formData.type === "pdf";

      // Validate file type
      if (isVideo && !file.type.startsWith("video/")) {
        setErrors({ ...errors, url: "Please select a valid video file" });
        return;
      }
      if (isAudio && !file.type.startsWith("audio/")) {
        setErrors({ ...errors, url: "Please select a valid audio file" });
        return;
      }
      if (isPdf && file.type !== "application/pdf") {
        setErrors({ ...errors, url: "Please select a valid PDF file" });
        return;
      }

      // Validate file size (max 100MB for mobile compatibility)
      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        setErrors({
          ...errors,
          url: `${
            isVideo ? "Video" : isAudio ? "Audio" : "PDF"
          } file size must be less than 100MB`,
        });
        return;
      }

      // Clean up previous object URL if it exists
      if (
        mediaPreview &&
        !isExistingMedia &&
        mediaPreview.startsWith("blob:")
      ) {
        URL.revokeObjectURL(mediaPreview);
      }

      setSelectedMedia(file);
      setMediaPreview(URL.createObjectURL(file));
      setIsExistingMedia(false);
      if (errors.url) {
        setErrors({ ...errors, url: "" });
      }
    }
  };

  const handleRemoveMedia = () => {
    // Clean up object URL if it was created for a new file
    if (selectedMedia && mediaPreview && !isExistingMedia) {
      URL.revokeObjectURL(mediaPreview);
    }
    setSelectedMedia(null);
    setMediaPreview(null);
    setIsExistingMedia(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setFormData({ ...formData, url: "" });
  };

  const handleFieldChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleErrorClear = (field: string) => {
    setErrors({ ...errors, [field]: "" });
  };

  const handleTypeChange = (
    newType: "article" | "video" | "audio" | "pdf" | "link"
  ) => {
    // Reset media when type changes away from video/audio/pdf
    if (
      (formData.type === "video" ||
        formData.type === "audio" ||
        formData.type === "pdf") &&
      newType !== "video" &&
      newType !== "audio" &&
      newType !== "pdf"
    ) {
      // Clean up object URL if it was created for a new file
      if (selectedMedia && mediaPreview && !isExistingMedia) {
        URL.revokeObjectURL(mediaPreview);
      }
      setSelectedMedia(null);
      setMediaPreview(null);
      setIsExistingMedia(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
    // Reset media when switching between video/audio/pdf or to video/audio/pdf
    if (
      formData.type !== "video" &&
      formData.type !== "audio" &&
      formData.type !== "pdf" &&
      (newType === "video" || newType === "audio" || newType === "pdf")
    ) {
      setSelectedMedia(null);
      setMediaPreview(null);
      setIsExistingMedia(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
    setFormData({
      ...formData,
      type: newType,
      url:
        newType === "video" || newType === "audio" || newType === "pdf"
          ? ""
          : formData.url,
    });
    // Clear URL error when type changes
    if (errors.url) {
      setErrors({ ...errors, url: "" });
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = "Resource title is required";
    } else if (formData.title.trim().length < 3) {
      newErrors.title = "Resource title must be at least 3 characters";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    } else if (formData.description.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    }

    if (!formData.shortDescription.trim()) {
      newErrors.shortDescription = "Short description is required";
    } else if (formData.shortDescription.trim().length < 5) {
      newErrors.shortDescription =
        "Short description must be at least 5 characters";
    }

    if (formData.duration < 0) {
      newErrors.duration = "Duration cannot be negative";
    } else if (formData.duration > 300) {
      newErrors.duration = "Duration cannot exceed 300 minutes";
    }

    // For video/audio/pdf types, check for file upload or existing URL
    if (
      formData.type === "audio" ||
      formData.type === "video" ||
      formData.type === "pdf"
    ) {
      if (!selectedMedia && !formData.url && !resource?.url) {
        newErrors.url = `${
          formData.type === "audio"
            ? "Audio"
            : formData.type === "video"
            ? "Video"
            : "PDF"
        } file is required`;
      } else if (
        formData.url.trim() &&
        !selectedMedia &&
        !isValidUrl(formData.url)
      ) {
        newErrors.url = "Please enter a valid URL or upload a file";
      }
    } else if (formData.url.trim() && !isValidUrl(formData.url)) {
      newErrors.url = "Please enter a valid URL";
    }

    if (
      (formData.type === "audio" || formData.type === "video") &&
      formData.duration <= 0
    ) {
      newErrors.duration = `${
        formData.type === "audio" ? "Audio" : "Video"
      } resources require a duration greater than 0`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const resourceData = {
        ...formData,
        tags: formData.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag),
        // Only include url if no new file is selected
        url: selectedMedia ? "" : formData.url,
      };
      onSave(resourceData, selectedMedia || undefined);
    }
  };

  return (
    <Box>
      {/* Basic Information Section */}
      <BasicInformationSection
        formData={{
          title: formData.title,
          shortDescription: formData.shortDescription,
          description: formData.description,
        }}
        errors={errors}
        onChange={handleFieldChange}
        onErrorClear={handleErrorClear}
      />

      {/* Resource Configuration Section */}
      <ResourceConfigurationSection
        formData={{
          type: formData.type,
          category: formData.category,
          duration: formData.duration,
          author: formData.author,
          source: formData.source,
          color: formData.color,
        }}
        errors={errors}
        onChange={handleFieldChange}
        onErrorClear={handleErrorClear}
        onTypeChange={handleTypeChange}
      />

      {/* Media Upload or URL Section */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {formData.type === "video" ||
            formData.type === "audio" ||
            formData.type === "pdf" ? (
              <MediaUploadSection
                type={formData.type}
                selectedMedia={selectedMedia}
                mediaPreview={mediaPreview}
                uploadProgress={uploadProgress}
                error={errors.url}
                fileInputRef={fileInputRef}
                onFileSelect={handleMediaSelect}
                onRemove={handleRemoveMedia}
              />
            ) : (
              <TextField
                fullWidth
                label='URL'
                placeholder='https://example.com/resource'
                value={formData.url}
                onChange={(e) => {
                  handleFieldChange("url", e.target.value);
                  if (errors.url) handleErrorClear("url");
                }}
                error={!!errors.url}
                helperText={errors.url || "External link to the resource"}
                InputProps={{
                  startAdornment: (
                    <Box
                      component='span'
                      sx={{ mr: 1, color: "text.secondary" }}
                    >
                      🔗
                    </Box>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    minHeight: 64,
                  },
                }}
              />
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Tags and Settings Section */}
      <TagsAndSettingsSection
        formData={{
          tags: formData.tags,
          content: formData.content,
          isActive: formData.isActive,
        }}
        onChange={handleFieldChange}
      />

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
        <Button
          onClick={onCancel}
          variant='outlined'
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={saving}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
            },
          }}
        >
          {saving
            ? "Saving..."
            : resource
            ? "Update Resource"
            : "Create Resource"}
        </Button>
      </Box>
    </Box>
  );
};

export default ResourceForm;
